import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A stubby trench mortar: a short wide tube angled steeply up on a square
// base plate, steadied by a bipod, with a few shells lying ready beside it.
const GUNMETAL = "#4a4d52";
const OLIVE = "#5f6b3c";
const BRASS = "#b08d3a";
const TIMBER = "#8b6438";

const ELEV = Math.PI / 3; // tube elevation (~60 degrees up from horizontal)
const TUBE_TILT = Math.PI / 2 - ELEV; // rotation.x that yields that elevation

// One shell lying on its side: an olive body with a brass nose cone.
function shell(x: number, z: number, yaw: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.13, 8), color: OLIVE,
      position: [x, 0.028, z], rotation: [Math.PI / 2, 0, yaw] },
    { geometry: new THREE.ConeGeometry(0.028, 0.05, 8), color: BRASS,
      position: [x + Math.sin(yaw) * 0.09, 0.028, z + Math.cos(yaw) * 0.09],
      rotation: [Math.PI / 2, 0, yaw] },
  ];
}

export const trenchMortar = defineModel({
  id: "trench-mortar",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  author: "Fable 5",
  name: "Trench Mortar",
  tags: ["prop", "wwi", "military", "artillery", "weapon"],
  summary: "A stubby mortar tube on a base plate, shells lying beside it.",
  footprint: 0.35,
  parts(): Part[] {
    return [
      // Square base plate the tube socket sits on.
      { geometry: new THREE.BoxGeometry(0.32, 0.035, 0.32), color: GUNMETAL, position: [-0.06, 0.018, -0.04] },
      // Short wide tube, angled ~60 degrees up towards +Z.
      { geometry: new THREE.CylinderGeometry(0.062, 0.07, 0.38, 10), color: GUNMETAL,
        position: [-0.06, 0.2, 0.05], rotation: [TUBE_TILT, 0, 0] },
      // Brass muzzle ring at the top of the tube.
      { geometry: new THREE.CylinderGeometry(0.068, 0.068, 0.03, 10), color: BRASS,
        position: [-0.06, 0.36, 0.14], rotation: [TUBE_TILT, 0, 0] },
      // Bipod: two legs from mid-tube down to the ground in front, with a
      // cross-bar and an elevation screw block where they meet the tube.
      { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.28, 6), color: GUNMETAL,
        position: [-0.16, 0.13, 0.16], rotation: [-0.35, 0, -0.35] },
      { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.28, 6), color: GUNMETAL,
        position: [0.04, 0.13, 0.16], rotation: [-0.35, 0, 0.35] },
      { geometry: new THREE.CylinderGeometry(0.01, 0.01, 0.2, 6), color: GUNMETAL,
        position: [-0.06, 0.2, 0.12], rotation: [0, 0, Math.PI / 2] },
      { geometry: new THREE.BoxGeometry(0.05, 0.06, 0.04), color: GUNMETAL,
        position: [-0.06, 0.24, 0.1], rotation: [TUBE_TILT, 0, 0] },
      // A small ammo crate and three shells laid out beside the mortar.
      { geometry: new THREE.BoxGeometry(0.12, 0.07, 0.16), color: TIMBER, position: [0.28, 0.035, -0.18] },
      ...shell(0.26, 0.08, 0.25),
      ...shell(0.32, 0.02, 0.2),
      ...shell(0.2, 0.14, 0.45),
    ];
  },
  animations: [],
});
